import { useState, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { FormField, Label, Input, FormError } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Select } from '../ui/select-custom';
import { fetchQuote, quotesConfigured } from '../../lib/quotes';
import { investmentTypes } from '../../types';
import type { InvestmentAsset, InvestmentType } from '../../types';
import type { ComponentProps } from 'react';

export interface InvestmentAssetFormData {
  ticker: string;
  type: InvestmentType;
  name: string;
  cnpj: string;
  currentPrice: number;
}

export interface InvestmentAssetFormProps
  extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (data: InvestmentAssetFormData) => void;
  onClose?: () => void;
  initialData?: InvestmentAsset | null;
  existingAssets?: InvestmentAsset[];
}

/** Máscara de CNPJ: 00.000.000/0000-00 */
function maskCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

/** FII termina em 11; o resto é tratado como ação. Tesouro é escolha manual. */
function guessType(ticker: string): InvestmentType {
  return /11B?$/.test(ticker.toUpperCase()) ? 'fii' : 'acao';
}

export function InvestmentAssetForm({
  onSubmit,
  onClose,
  initialData,
  existingAssets = [],
  ...props
}: InvestmentAssetFormProps) {
  const [ticker, setTicker] = useState(initialData?.ticker ?? '');
  const [type, setType] = useState<InvestmentType>(initialData?.type ?? 'acao');
  const [name, setName] = useState(initialData?.name ?? '');
  const [cnpj, setCnpj] = useState(initialData?.cnpj ?? '');
  const [currentPrice, setCurrentPrice] = useState(initialData?.currentPrice ?? 0);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const isEditing = !!initialData;

  useEffect(() => {
    setTicker(initialData?.ticker ?? '');
    setType(initialData?.type ?? 'acao');
    setName(initialData?.name ?? '');
    setCnpj(initialData?.cnpj ?? '');
    setCurrentPrice(initialData?.currentPrice ?? 0);
    setLookupError(null);
  }, [initialData]);

  const normalizedTicker = ticker.trim().toUpperCase();

  const isDuplicate = existingAssets.some(
    (a) => a.id !== initialData?.id && a.ticker.toUpperCase() === normalizedTicker
  );

  // Título do Tesouro não tem ticker de bolsa: o próprio nome identifica.
  const isFormValid =
    (type === 'tesouro' ? normalizedTicker.length > 0 : normalizedTicker.length >= 4) &&
    name.trim() !== '' &&
    !isDuplicate;

  const handleTickerChange = (value: string) => {
    // Tesouro usa o nome como identificador, então aceita espaço e "+".
    const upper =
      type === 'tesouro'
        ? value.toUpperCase()
        : value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setTicker(upper);
    setLookupError(null);
    // Só sugere o tipo no cadastro novo, para não sobrescrever escolha manual.
    if (!isEditing && upper.length >= 5) setType(guessType(upper));
  };

  const handleLookup = async () => {
    if (!normalizedTicker) return;
    setLookingUp(true);
    setLookupError(null);
    try {
      const quote = await fetchQuote(normalizedTicker);
      if (!quote) {
        setLookupError('Ticker não encontrado na brapi.');
        return;
      }
      setCurrentPrice(quote.price);
      if (quote.name) setName(quote.name);
    } catch (error) {
      setLookupError(error instanceof Error ? error.message : 'Falha na busca.');
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    onSubmit({
      ticker: normalizedTicker,
      type,
      name: name.trim(),
      cnpj: cnpj.trim(),
      currentPrice,
    });
  };

  return (
    <form
      data-slot="investment-asset-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      {...props}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label htmlFor="ticker">
            Papel <span className="text-destructive">*</span>
          </Label>
          <div className="flex gap-2">
            <Input
              id="ticker"
              value={ticker}
              placeholder={type === 'tesouro' ? 'Ex: IPCA+ 2029' : 'Ex: PETR4'}
              onChange={(e) => handleTickerChange(e.target.value)}
              error={isDuplicate}
              maxLength={type === 'tesouro' ? 60 : 8}
              required
            />
            {quotesConfigured && type !== 'tesouro' && (
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={handleLookup}
                disabled={!normalizedTicker || lookingUp}
                title="Buscar nome e cotação"
                className="shrink-0 px-3"
              >
                {lookingUp ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Search className="size-4" />
                )}
              </Button>
            )}
          </div>
          {isDuplicate && <FormError>Esse papel já está cadastrado.</FormError>}
          {lookupError && <FormError>{lookupError}</FormError>}
        </FormField>

        <FormField>
          <Label htmlFor="type">
            Tipo <span className="text-destructive">*</span>
          </Label>
          <Select
            value={type}
            onChange={(value) => setType(value as InvestmentType)}
            options={(Object.keys(investmentTypes) as InvestmentType[]).map((key) => ({
              value: key,
              label: investmentTypes[key].shortLabel,
            }))}
            className="w-full justify-between"
          />
        </FormField>
      </div>

      <FormField>
        <Label htmlFor="name">
          Nome da empresa / fundo <span className="text-destructive">*</span>
        </Label>
        <Input
          id="name"
          value={name}
          placeholder="Ex: Petróleo Brasileiro S.A."
          onChange={(e) => setName(e.target.value)}
          required
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label htmlFor="cnpj">CNPJ</Label>
          <Input
            id="cnpj"
            value={cnpj}
            placeholder="00.000.000/0000-00"
            onChange={(e) => setCnpj(maskCnpj(e.target.value))}
            inputMode="numeric"
            maxLength={18}
          />
          <span className="text-xs text-muted-foreground">
            Exigido nas fichas do IRPF.
          </span>
        </FormField>

        <FormField>
          <Label htmlFor="currentPrice">
            {type === 'tesouro' ? 'Valor atual (PU)' : 'Valor atual (cotação)'}
          </Label>
          <CurrencyInput
            id="currentPrice"
            value={currentPrice}
            onChange={setCurrentPrice}
          />
          {initialData?.priceUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              Atualizado em{' '}
              {initialData.priceUpdatedAt.toLocaleDateString('pt-BR')}
            </span>
          )}
        </FormField>
      </div>

      <div className="flex gap-2">
        {onClose && (
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={!isFormValid}>
          {isEditing ? 'Salvar alterações' : 'Cadastrar papel'}
        </Button>
      </div>
    </form>
  );
}
